import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
    selector: 'app-user-session',
    standalone: true,
    imports: [ConfirmDialog, DialogModule, ButtonModule],
    providers: [ConfirmationService],
    template: `
    <div class="flex items-center gap-6 p-4 rounded-lg font-sans">
      <img
        class="rounded-xl w-8 h-8"
        src="/images/avatar.png"
        alt="Profile"
      />
      <div class="flex flex-col">
        <span class="text-sm ">{{ username }}</span>
        <div class="flex items-center gap-2">
          <span class="text-xs">Tempo restante:</span>
          <span class="text-sm font-semibold ">{{ remainingTime }}</span>
        </div>
      </div>
    </div>
    <p-confirmdialog key="session" position="center"></p-confirmdialog>
    `
})
export class UserSessionComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private kc = inject(Keycloak);
    private confirmationService = inject(ConfirmationService);

    username        = '';
    remainingTime   = '';
    countdownSeconds = 0;
    private dialogShownOnce = false;

    ngOnInit() {
        this.loadProfile();
        interval(1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateCountdown());
    }

    private loadProfile(): void {
        const token = this.kc.tokenParsed as KeycloakTokenParsed;
        this.username = (token['name'] as string) || '';
        this.updateCountdown();
    }

    private updateCountdown(): void {
        const exp = (this.kc.tokenParsed as any)?.exp as number | undefined;
        if (!exp) {
            this.remainingTime = '';
            return;
        }

        const secs = exp - Math.floor(Date.now() / 1000);
        this.countdownSeconds = secs;
        this.remainingTime = secs > 0
            ? this.formatTime(secs)
            : 'Sessão expirada';

        if (secs <= 1) {
            this.kc.logout().then();
            return;
        }

        // Abre o diálogo apenas uma vez quando faltar ≤30s (e >2s, para não bater com o logout acima)
        if (secs <= 30 && secs > 2 && !this.dialogShownOnce) {
            this.dialogShownOnce = true;
            this.confirmationService.confirm({
                message: `Faltam ${secs} segundos para expirar.`,
                header: 'Sessão expirando',
                icon: 'pi pi-info-circle',
                rejectButtonStyleClass: 'p-button-text',
                rejectButtonProps: {
                    label: 'Sair',
                    severity: 'secondary',
                    text: true,
                },
                acceptButtonProps: {
                    label: 'Renovar',
                    text: true,
                },
                accept: () => {
                    this.kc.updateToken(30)
                        .then(() => this.updateCountdown())
                        .catch(() => this.kc.logout());
                },
                reject: () => {
                    this.kc.logout().then();
                },
                key: 'session',
            });
        }
    }


    logout(): void {
        this.kc.logout().then();
    }

    private formatTime(total: number): string {
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return [h, m, s]
            .map(n => n.toString().padStart(2, '0'))
            .join(':');
    }
}
